// src/services/dashboard.service.ts

import api from './api';
import type { 
  ApiResponse,
  CountryOverviewResponse,
  CountryFinancialResponse,
  CountryMembershipResponse,
  CountryPerformanceResponse,
  CountryTrendsResponse,
  RegionalDrilldownResponse
} from '../types/api.types';

export interface DashboardKPIs {
  totalMembers: number;
  activeMembers: number;
  totalSavings: number;
  totalCredits: number;
  solidarityFund: number;
  monthlyGrowth: {
    members: number;
    savings: number;
    credits: number;
  };
}

export interface RecentActivity {
  id: string;
  type: 'deposit' | 'withdrawal' | 'credit' | 'repayment' | 'contribution' | 'solidarity';
  description: string;
  amount: number;
  memberName: string;
  date: string;
  status: string;
}

export interface FinancialSummary {
  totalAssets: number;
  totalLiabilities: number;
  equity: number;
  cashOnHand: number;
  pendingTransactions: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  netIncome: number;
}

export interface GroupHealth {
  score: number;
  rating: 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Critical';
  factors: {
    membershipHealth: number;
    financialHealth: number;
    contributionHealth: number;
    meetingHealth: number;
  };
}

export interface PerformanceMetrics {
  savingsGrowthRate: number;
  creditRecoveryRate: number;
  memberRetentionRate: number;
  contributionCollectionRate: number;
  meetingAttendanceRate: number;
  portfolioYield: number;
}

export interface TrendData {
  period: string;
  savings: number;
  credits: number;
  members: number;
  income: number;
  expenses: number;
}

export interface DashboardData {
  kpis: DashboardKPIs;
  recentActivity: RecentActivity[];
  financialSummary: FinancialSummary;
  groupHealth: GroupHealth;
  performanceMetrics: PerformanceMetrics;
  trends: {
    monthly: TrendData[];
    weekly: TrendData[];
  };
  alerts: Array<{
    id: string;
    type: 'warning' | 'error' | 'info';
    title: string;
    message: string;
    date: string;
  }>;
}

export interface BalanceSheet {
  assets: {
    cash: number;
    savingsAccount: number;
    loansReceivable: number;
    otherAssets: number;
    total: number;
  };
  liabilities: {
    memberSavings: number;
    solidarityFund: number;
    otherLiabilities: number;
    total: number;
  };
  equity: {
    retainedEarnings: number;
    currentEarnings: number;
    total: number;
  };
  healthMetrics: {
    debtToEquityRatio: number;
    equityRatio: number;
    isBalanced: boolean;
  };
}

export interface IncomeStatement {
  revenue: {
    interestIncome: number;
    feeIncome: number;
    otherIncome: number;
    total: number;
  };
  expenses: {
    operatingExpenses: number;
    adminExpenses: number;
    otherExpenses: number;
    total: number;
  };
  netIncome: number;
  margins: {
    grossMargin: number;
    netMargin: number;
  };
}

export interface CreditPortfolio {
  totalCredits: number;
  totalOutstanding: number;
  totalDisbursed: number;
  totalRepaid: number;
  portfolioClassification: {
    performing: { count: number; amount: number; percentage: number };
    watchlist: { count: number; amount: number; percentage: number };
    substandard: { count: number; amount: number; percentage: number };
    doubtful: { count: number; amount: number; percentage: number };
    loss: { count: number; amount: number; percentage: number };
  };
  topBorrowers: Array<{
    memberName: string;
    amount: number;
    status: string;
  }>;
  overdueCredits: Array<{
    memberName: string;
    amount: number;
    daysPastDue: number;
  }>;
}

export interface MembersAnalytics {
  totalMembers: number;
  activeMembers: number;
  inactiveMembers: number;
  newMembersThisMonth: number;
  demographics: {
    byGender: { male: number; female: number };
    byAge: Array<{ ageGroup: string; count: number }>;
  };
  memberPerformance: Array<{
    memberName: string;
    savingsBalance: number;
    creditBalance: number;
    lastActivity: string;
    attendanceRate: number;
  }>;
  retentionMetrics: {
    monthlyRetentionRate: number;
    yearlyRetentionRate: number;
    churnRate: number;
  };
}

export interface GroupAnalytics {
  groupId: string;
  groupName: string;
  dashboard: DashboardData;
  memberPerformance: Array<{
    memberId: string;
    memberName: string;
    savingsBalance: number;
    creditBalance: number;
    contributionRate: number;
    attendanceRate: number;
    healthScore: number;
  }>;
  healthScore: GroupHealth;
  trends: {
    membershipTrend: TrendData[];
    financialTrend: TrendData[];
    activityTrend: TrendData[];
  };
}

export interface ComparisonData {
  entityId: string;
  entityName: string;
  metrics: {
    totalMembers: number;
    totalSavings: number;
    totalCredits: number;
    healthScore: number;
    growthRate: number;
  };
}

class DashboardService {
  /**
   * Get main dashboard data for a group
   */
  async getGroupDashboard(groupId?: string): Promise<ApiResponse<DashboardData>> {
    const endpoint = groupId ? `/analytics/groups/${groupId}/dashboard` : '/reports/dashboard';
    const response = await api.get(endpoint);
    return response;
  }

  /**
   * Get group analytics (comprehensive group analysis)
   */
  async getGroupAnalytics(groupId: string): Promise<ApiResponse<GroupAnalytics>> {
    const response = await api.get(`/analytics/groups/${groupId}/dashboard`);
    return response;
  }

  /**
   * Get commune-level dashboard
   */
  async getCommuneDashboard(communeId: string): Promise<ApiResponse<DashboardData>> {
    const response = await api.get(`/analytics/communes/${communeId}/dashboard`);
    return response;
  }

  /**
   * Get system-wide dashboard
   */
  async getSystemDashboard(): Promise<ApiResponse<DashboardData>> {
    const response = await api.get('/analytics/system/dashboard');
    return response;
  }

  /**
   * Get system-wide communes overview for super admin
   */
  async getSystemCommunesOverview(): Promise<ApiResponse<{
    totalCommunes: number;
    activeCommunes: number;
    totalGroupsAcrossCommunes: number;
    totalMembersAcrossCommunes: number;
    topPerformingCommunes: Array<{
      communeId: string;
      communeName: string;
      totalGroups: number;
      totalMembers: number;
      totalSavings: number;
      region: string;
    }>;
    communesByRegion: Array<{
      region: string;
      communeCount: number;
      totalGroups: number;
      totalMembers: number;
    }>;
    recentCommuneActivity: Array<{
      communeId: string;
      communeName: string;
      activity: string;
      timestamp: string;
    }>;
  }>> {
    const response = await api.get('/communes/summary');
    return response;
  }

  /**
   * Get balance sheet report
   */
  async getBalanceSheet(groupId?: string): Promise<ApiResponse<BalanceSheet>> {
    const params = groupId ? `?groupId=${groupId}` : '';
    const response = await api.get(`/reports/balance-sheet${params}`);
    return response;
  }

  /**
   * Get income statement report
   */
  async getIncomeStatement(
    groupId?: string, 
    startDate?: string, 
    endDate?: string
  ): Promise<ApiResponse<IncomeStatement>> {
    const params = new URLSearchParams();
    if (groupId) params.append('groupId', groupId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/reports/income-statement${query}`);
    return response;
  }

  /**
   * Get credit portfolio analysis
   */
  async getCreditPortfolio(groupId?: string): Promise<ApiResponse<CreditPortfolio>> {
    const params = groupId ? `?groupId=${groupId}` : '';
    const response = await api.get(`/reports/credits-portfolio${params}`);
    return response;
  }

  /**
   * Get members analytics
   */
  async getMembersAnalytics(groupId?: string): Promise<ApiResponse<MembersAnalytics>> {
    const params = groupId ? `?groupId=${groupId}` : '';
    const response = await api.get(`/reports/members-summary${params}`);
    return response;
  }

  /**
   * Get savings report
   */
  async getSavingsReport(
    groupId?: string, 
    startDate?: string, 
    endDate?: string
  ): Promise<ApiResponse<{
    totalDeposits: number;
    totalWithdrawals: number;
    netSavings: number;
    memberStatistics: Array<{
      memberName: string;
      balance: number;
      monthlyDeposits: number;
    }>;
    monthlyTrend: Array<{
      month: string;
      deposits: number;
      withdrawals: number;
      net: number;
    }>;
  }>> {
    const params = new URLSearchParams();
    if (groupId) params.append('groupId', groupId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/reports/savings${query}`);
    return response;
  }

  /**
   * Get solidarity fund report
   */
  async getSolidarityReport(groupId?: string): Promise<ApiResponse<{
    fundBalance: number;
    totalContributions: number;
    totalDisbursements: number;
    pendingRequests: number;
    approvedRequests: number;
    requestsByCategory: Array<{
      category: string;
      count: number;
      amount: number;
    }>;
    recentDisbursements: Array<{
      memberName: string;
      amount: number;
      reason: string;
      date: string;
    }>;
  }>> {
    const params = groupId ? `?groupId=${groupId}` : '';
    const response = await api.get(`/reports/solidarity${params}`);
    return response;
  }

  /**
   * Get meetings report
   */
  async getMeetingsReport(
    groupId?: string, 
    startDate?: string, 
    endDate?: string
  ): Promise<ApiResponse<{
    totalMeetings: number;
    averageAttendance: number;
    memberAttendance: Array<{
      memberName: string;
      attendanceRate: number;
      meetingsAttended: number;
      totalMeetings: number;
    }>;
    attendanceTrend: Array<{
      date: string;
      attendanceRate: number;
      attendeeCount: number;
    }>;
  }>> {
    const params = new URLSearchParams();
    if (groupId) params.append('groupId', groupId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/reports/meetings${query}`);
    return response;
  }

  /**
   * Get comparative analysis between groups
   */
  async getComparativeAnalysis(
    groupIds: string[], 
    metrics: string[] = ['members', 'savings', 'credits', 'health']
  ): Promise<ApiResponse<{
    comparison: ComparisonData[];
    benchmarks: {
      industryAverage: ComparisonData;
      topPerformer: ComparisonData;
    };
  }>> {
    const response = await api.post('/analytics/groups/comparative', {
      groupIds,
      metrics,
    });
    return response;
  }

  /**
   * Get performance metrics for a specific entity
   */
  async getPerformanceMetrics(
    level: 'group' | 'commune' | 'system',
    entityId: string,
    period: 'week' | 'month' | 'quarter' | 'year' = 'month'
  ): Promise<ApiResponse<PerformanceMetrics & {
    trends: TrendData[];
    benchmarks: {
      previousPeriod: PerformanceMetrics;
      industryAverage: PerformanceMetrics;
    };
  }>> {
    const response = await api.get(`/analytics/performance/${level}/${entityId}?period=${period}`);
    return response;
  }

  /**
   * Get real-time analytics
   */
  async getRealTimeAnalytics(): Promise<ApiResponse<{
    currentActivity: {
      activeUsers: number;
      transactionsToday: number;
      depositsToday: number;
      creditsToday: number;
    };
    systemStatus: {
      uptime: number;
      responseTime: number;
      errorRate: number;
    };
    liveMetrics: {
      totalTransactionValue: number;
      averageTransactionSize: number;
      peakHour: string;
    };
  }>> {
    const response = await api.get('/analytics/realtime');
    return response;
  }

  /**
   * Export dashboard data
   */
  async exportDashboardData(
    format: 'json' | 'csv' | 'excel',
    entityId?: string,
    entityType: 'group' | 'commune' | 'system' = 'group'
  ): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', format);
    if (entityId) params.append('entityId', entityId);
    params.append('entityType', entityType);

    const response = await api.getAxiosInstance().get(
      `/analytics/export?${params.toString()}`,
      { responseType: 'blob' }
    );
    
    return response.data;
  }

  /**
   * Format currency amount
   */
  formatAmount(amount: number): string {
    return new Intl.NumberFormat('fr-TG', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(amount);
  }

  /**
   * Format percentage
   */
  formatPercentage(value: number, decimals: number = 1): string {
    return `${value.toFixed(decimals)}%`;
  }

  /**
   * Get health score color
   */
  getHealthScoreColor(score: number): string {
    if (score >= 80) return '#52c41a'; // Green
    if (score >= 60) return '#1890ff'; // Blue
    if (score >= 40) return '#faad14'; // Orange
    if (score >= 20) return '#fa8c16'; // Dark Orange
    return '#ff4d4f'; // Red
  }

  /**
   * Get health score rating
   */
  getHealthScoreRating(score: number): string {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    if (score >= 20) return 'Poor';
    return 'Critical';
  }

  /**
   * Calculate trend percentage
   */
  calculateTrendPercentage(current: number, previous: number): number {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  }

  /**
   * Generate period options for reports
   */
  getPeriodOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'week', label: 'Cette semaine' },
      { value: 'month', label: 'Ce mois' },
      { value: 'quarter', label: 'Ce trimestre' },
      { value: 'year', label: 'Cette année' },
      { value: 'custom', label: 'Période personnalisée' },
    ];
  }

  /**
   * Get default date range for period
   */
  getDateRangeForPeriod(period: string): { startDate: string; endDate: string } {
    const now = new Date();
    let startDate: Date;
    const endDate = now;

    switch (period) {
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarterStart = Math.floor(now.getMonth() / 3) * 3;
        startDate = new Date(now.getFullYear(), quarterStart, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  }

  // =====================================================
  // COUNTRY ANALYTICS METHODS
  // =====================================================

  /**
   * Get country overview analytics
   */
  async getCountryOverview(): Promise<CountryOverviewResponse> {
    const response = await api.get('/analytics/country/overview');
    return response.data;
  }

  /**
   * Get country financial analytics
   */
  async getCountryFinancial(): Promise<CountryFinancialResponse> {
    const response = await api.get('/analytics/country/financial');
    return response.data;
  }

  /**
   * Get country membership analytics
   */
  async getCountryMembership(): Promise<CountryMembershipResponse> {
    const response = await api.get('/analytics/country/membership');
    return response.data;
  }

  /**
   * Get country performance benchmarks
   */
  async getCountryPerformance(): Promise<CountryPerformanceResponse> {
    const response = await api.get('/analytics/country/performance');
    return response.data;
  }

  /**
   * Get country trends analysis
   */
  async getCountryTrends(): Promise<CountryTrendsResponse> {
    const response = await api.get('/analytics/country/trends');
    return response.data;
  }

  /**
   * Get regional drill-down data
   */
  async getRegionalDrilldown(regionId: string): Promise<RegionalDrilldownResponse> {
    const response = await api.get(`/analytics/country/geographic/${regionId}`);
    return response.data;
  }

  /**
   * Format large numbers for display
   */
  formatLargeNumber(value: number): string {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  }

  /**
   * Get performance score color based on score
   */
  getPerformanceScoreColor(score: number): string {
    if (score >= 0.8) return '#52c41a'; // Green
    if (score >= 0.6) return '#1890ff'; // Blue
    if (score >= 0.4) return '#faad14'; // Orange
    if (score >= 0.2) return '#fa8c16'; // Dark Orange
    return '#ff4d4f'; // Red
  }

  /**
   * Get risk level color
   */
  getRiskLevelColor(risk: string): string {
    switch (risk.toLowerCase()) {
      case 'low':
        return '#52c41a'; // Green
      case 'medium':
        return '#faad14'; // Orange
      case 'high':
        return '#ff4d4f'; // Red
      default:
        return '#d9d9d9'; // Grey
    }
  }
}

export default new DashboardService();